// Publications data service - now uses JSON instead of BibTeX for better reliability

export interface Publication {
  id: string;
  title: string;
  authors: string;
  venue: string;
  year: number;
  type: 'journal' | 'conference' | 'preprint' | 'book' | 'chapter' | 'nato-journal' | 'magazine';
  doi?: string;
  arxiv?: string;
  /** Self-hosted copy, e.g. /content/papers/<id>.pdf — preferred over publisher links */
  pdf?: string;
  abstract?: string;
  pages?: string;
  volume?: string;
  publisher?: string;
  location?: string;
  url?: string;
  featured?: boolean;
  tagline?: string;
  figure?: string;
  /** Figures shown in the featured card, in display order */
  figures?: PublicationFigure[];
  /** Publication status for papers not yet formally published */
  status?: 'to-appear' | 'in-press' | 'under-review';
}

export interface PublicationFigure {
  src: string;
  alt: string;
  caption: string;
}

// Author name as it appears in author lists, used to emphasise own authorship
export const OWN_AUTHOR_NAME = 'Maaiveld, T. M.';

// Fallback destination when a publication has no direct full-text link of its own
export const RESEARCHGATE_PROFILE = 'https://www.researchgate.net/profile/Thomas-Maaiveld/research';

// Note: Cache removed for immediate content updates

// Note: BibTeX parsing removed in favor of JSON for better reliability

// Fetch publications from JSON file
export const fetchPublications = async (): Promise<Publication[]> => {
  try {
    const response = await fetch(`/content/publications.json?t=${Date.now()}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch publications: ${response.statusText}`);
    }
    
    const data = await response.json();
    console.log('Publications loaded from JSON:', data.publications.length);
    
    return data.publications as Publication[];
  } catch (error) {
    console.error('Error fetching publications:', error);
    // Return fallback data if parsing fails
    return getFallbackPublications();
  }
};

// Fallback publications data in case BibTeX parsing fails
const getFallbackPublications = (): Publication[] => {
  return [
    {
      id: "maaiveld2026llm",
      title: "Large Language Models for Defense and Security: A Systematic Review",
      authors: "Maaiveld, T. M. and Perez Zambrano, N. and Trantas, A. and Weyland, L. and Hentenaar, C. and Domela Nieuwenhuis Nyegaard, D. and Schadd, M. P. D.",
      venue: "SSRN",
      year: 2026,
      type: "preprint" as const,
      url: "https://ssrn.com/abstract=7509620",
      doi: "10.2139/ssrn.7509620"
    },
    {
      id: "maaiveld2026tactical",
      title: "A Customizable Tactical Engine for Mission Planning with Probabilistic Inference",
      authors: "Maaiveld, T. M. and Wolff, E. and Domela Nieuwenhuis Nyegaard, D. and Visker, O. and Jansen, K. and Voogd, J. M. and Schadd, M. P. D.",
      venue: "NATO Journal of Science and Technology",
      year: 2026,
      type: "nato-journal" as const,
      doi: "10.14339/STO-SAS-ORA-2025-6"
    },
    {
      id: "maaiveld2025enhanced",
      title: "Enhanced Crime Scripting: Using Ontologies for Comparative Analysis and Identifying Effective Interventions",
      authors: "Maaiveld, T. M. and Kalff, E. and Merkestein, D.",
      venue: "European Union Agency for Law Enforcement Training (CEPOL) Conference Proceedings 2025",
      year: 2025,
      type: "conference" as const,
      publisher: "CEPOL"
    },
    {
      id: "maaiveld2025geospatial",
      title: "Towards Geospatial Decision Support for Dismounted Cold-Weather Operations",
      authors: "Maaiveld, T. M. and Schadd, M. P. D. and van der Sanden, K. P. H. M. and Domela Nieuwenhuis Nyegaard, D. and Houben, M. and de Vrijer, A. and Kingma, B. R. M.",
      venue: "2025 International Conference on Military Communication and Information Systems (ICMCIS)",
      year: 2025,
      type: "conference" as const,
      publisher: "IEEE",
      location: "Piscataway, NJ, USA",
      doi: "10.1109/ICMCIS64378.2025.11047832"
    },
    {
      id: "merkestein2024crime",
      title: "Crime Scripting Improves the Approach to Undermining Crime",
      authors: "Merkestein, D. and Maaiveld, T. M. and Goldbach, O.",
      venue: "Vaktijdschrift Secondant",
      year: 2024,
      type: "magazine" as const,
      publisher: "Centrum voor Criminaliteitspreventie en Veiligheid (CCV)",
      url: "https://ccv-secondant.nl/platform/article/crime-scripting-verbetert-aanpak-van-ondermijnende-criminaliteit"
    },
    {
      id: "dereus2024generating",
      title: "Generating Explainable Military Courses of Action",
      authors: "de Reus, N. and Schadd, M. P. D. and Maaiveld, T. M. and Domela Nieuwenhuis Nyegaard, D. and van den Broek, A. A. and van der Waa, J. and Trantas, A.",
      venue: "2024 International Conference on Military Communication and Information Systems (ICMCIS)",
      year: 2024,
      type: "conference" as const,
      publisher: "IEEE",
      location: "Piscataway, NJ, USA",
      pages: "1--10",
      doi: "10.1109/ICMCIS61231.2024.10540875"
    },
    {
      id: "kalff2024survey",
      title: "Survey on Sensing, Modelling and Reasoning Aspects in Military Autonomous Systems",
      authors: "Kalff, E. F. and Maaiveld, T. M. and Schadd, M. P. D. and Voogd, J. M. and Weyland, L. F.",
      venue: "Modelling and Simulation for Autonomous Systems. MESAS 2023. Lecture Notes in Computer Science",
      year: 2024,
      type: "chapter" as const,
      publisher: "Springer, Cham",
      volume: "14615",
      doi: "10.1007/978-3-031-71397-2_17"
    },
    {
      id: "jabeen2020victim",
      title: "Victim No More: A Temporal-Causal Analysis of How a Mediator Can Help Out a Bullying Victim",
      authors: "Jabeen, F. and Nirzalla, N. and Maaiveld, T. M. and Gerritsen, C. and Treur, J.",
      venue: "Journal of Information and Telecommunication",
      year: 2020,
      type: "journal" as const,
      doi: "10.1080/24751839.2020.1724739"
    },
    {
      id: "maaiveld2024where",
      title: "Where to Go and How to Get There: Tactical Terrain Analysis for Military Unmanned Ground-Vehicle Mission Planning",
      authors: "Maaiveld, T. M. and Domela Nieuwenhuis Nyegaard, D. O. and de Reus, N. M. and Schadd, M. P. D. and Kuijper, F.",
      venue: "Modelling and Simulation for Autonomous Systems. MESAS 2023. Lecture Notes in Computer Science",
      year: 2024,
      type: "chapter" as const,
      publisher: "Springer, Cham",
      volume: "14615",
      doi: "10.1007/978-3-031-71397-2_7"
    }
  ];
};

// Helper function to get publication type label
export const getTypeLabel = (type: string): string => {
  switch (type) {
    case 'journal': return 'Journal Article';
    case 'conference': return 'Conference Paper';
    case 'preprint': return 'Preprint';
    case 'book': return 'Book';
    case 'chapter': return 'Book Chapter';
    case 'nato-journal': return 'Journal Article';
    case 'magazine': return 'Magazine Article';
    default: return type;
  }
};

// Helper function to get publication type CSS class.
// nato-journal shares the journal styling so the same label never renders two ways.
export const getTypeClass = (type: string): string => {
  const styleKey = type === 'nato-journal' ? 'journal' : type;
  return `publication-type publication-type-${styleKey}`;
};

// Helper function to get publication status label
export const getStatusLabel = (status: string): string => {
  switch (status) {
    case 'to-appear': return 'To Appear';
    case 'in-press': return 'In Press';
    case 'under-review': return 'Under Review';
    default: return status;
  }
};

// Best available destination for reading the paper itself.
// Prefers our own hosted PDF (public/content/papers/) so readers land on the
// paper rather than a publisher landing or issue page, then a full-text URL,
// then the publisher page via DOI. Unpublished work with no link of its own
// returns undefined so the title renders unlinked; anything else falls back to
// the ResearchGate profile where the full text can be requested.
export const getPrimaryLink = (pub: Publication): string | undefined => {
  if (pub.pdf) return pub.pdf;
  if (pub.url) return pub.url;
  if (pub.doi) return `https://doi.org/${pub.doi}`;
  if (pub.status) return undefined;
  return RESEARCHGATE_PROFILE;
};

// Generate a BibTeX entry for a publication
export const generateBibTeXEntry = (pub: Publication): string => {
  const getBibTeXType = (type: string): string => {
    switch (type) {
      case 'journal':
      case 'nato-journal':
      case 'magazine': return 'article';
      case 'conference': return 'inproceedings';
      case 'chapter': return 'incollection';
      case 'book': return 'book';
      case 'preprint': return 'misc';
      default: return 'misc';
    }
  };

  const bibType = getBibTeXType(pub.type);
  let entry = `@${bibType}{${pub.id},\n`;
  entry += `  title={${pub.title}},\n`;
  entry += `  author={${pub.authors}},\n`;
  
  if (pub.type === 'journal' || pub.type === 'nato-journal' || pub.type === 'magazine') {
    entry += `  journal={${pub.venue}},\n`;
  } else if (pub.type === 'conference' || pub.type === 'chapter') {
    entry += `  booktitle={${pub.venue}},\n`;
  } else if (pub.type === 'preprint') {
    entry += `  howpublished={${pub.venue}},\n`;
  }
  
  entry += `  year={${pub.year}},\n`;
  
  if (pub.publisher) {
    entry += `  publisher={${pub.publisher}},\n`;
  }
  
  if (pub.doi) {
    entry += `  doi={${pub.doi}},\n`;
  }
  
  if (pub.pages) {
    entry += `  pages={${pub.pages}},\n`;
  }
  
  if (pub.volume) {
    entry += `  volume={${pub.volume}},\n`;
  }
  
  if (pub.location) {
    entry += `  address={${pub.location}},\n`;
  }
  
  if (pub.url) {
    entry += `  url={${pub.url}},\n`;
  }
  
  // Remove trailing comma and newline
  entry = entry.replace(/,\n$/, '\n');
  entry += '}';
  
  return entry;
};
